import { create } from "zustand";
import { applyTheme, resolvePalette } from "~/lib/theme";

/**
 * "real"  : 実データを使用（デフォルト）
 * "set"   : 写真設定済み状態を強制（コーチマーク非表示）
 * "unset" : 写真未設定状態を強制（コーチマーク表示）
 */
export type AvatarCoachmarkDebug = "real" | "set" | "unset";

const CYCLE: AvatarCoachmarkDebug[] = ["real", "set", "unset"];

/**
 * "real"     : 実データ（BEスケジュール）を使用（デフォルト）
 * "counting" : カウントダウン中状態を強制（実データを待たず見た目確認用）
 * "live"     : 「配信中」CTA状態を強制
 */
export type EimiCountdownDebug = "real" | "counting" | "live";

const EIMI_COUNTDOWN_CYCLE: EimiCountdownDebug[] = ["real", "counting", "live"];

/**
 * "real" : 実データ（get_user_inf の preferred_personalities）を使用（デフォルト）
 * "show" : 「あなたの好みに合う子」表示状態を強制
 * "hide" : 「あなたの好みに合う子」非表示状態を強制
 */
export type PreferenceMatchDebug = "real" | "show" | "hide";

const PREFERENCE_MATCH_CYCLE: PreferenceMatchDebug[] = ["real", "show", "hide"];

type DebugState = {
  avatarCoachmarkDebug: AvatarCoachmarkDebug;
  cycleAvatarCoachmarkDebug: () => void;
  /**
   * true の間、新規登録直後の遷移先を /onboarding ではなく /discover に固定する
   * （好みタイプ選択オンボーディング開発中、他機能の動作確認をしやすくするための一時的なローカル切り替え）
   */
  skipOnboardingRedirect: boolean;
  toggleSkipOnboardingRedirect: () => void;
  /** デバッグ用テーマ上書き（null = 実アプリのテーマをそのまま使用） */
  debugThemeAppId: string | null;
  setDebugThemeAppId: (appId: string | null) => void;
  /** true の間、プロフィール取得APIをcode:60(ブロック)エラーとして再現する */
  forceBlockedProfileError: boolean;
  toggleForceBlockedProfileError: () => void;
  /** true の間、プロフィール取得APIを未対応コードのエラー(汎用エラーモーダル表示)として再現する */
  forceUnknownProfileError: boolean;
  toggleForceUnknownProfileError: () => void;
  /** ライブ一覧のえいみキャンペーンカウントダウンバナー状態強制（キャンペーン期間外でも見た目確認できるように） */
  eimiCountdownDebug: EimiCountdownDebug;
  cycleEimiCountdownDebug: () => void;
  /** さがす画面「あなたの好みに合う子」表示ゲート状態強制（好み未保存アカウントでも見た目確認できるように） */
  preferenceMatchDebug: PreferenceMatchDebug;
  cyclePreferenceMatchDebug: () => void;
};

export const useDebugStore = create<DebugState>()((set) => ({
  avatarCoachmarkDebug: "real",
  cycleAvatarCoachmarkDebug: () =>
    set((s) => ({
      avatarCoachmarkDebug:
        CYCLE[(CYCLE.indexOf(s.avatarCoachmarkDebug) + 1) % CYCLE.length],
    })),
  debugThemeAppId: null,
  setDebugThemeAppId: (appId) => {
    applyTheme(resolvePalette(appId));
    set({ debugThemeAppId: appId });
  },
  skipOnboardingRedirect: false,
  toggleSkipOnboardingRedirect: () =>
    set((s) => ({ skipOnboardingRedirect: !s.skipOnboardingRedirect })),
  forceBlockedProfileError: false,
  toggleForceBlockedProfileError: () =>
    set((s) => ({ forceBlockedProfileError: !s.forceBlockedProfileError })),
  forceUnknownProfileError: false,
  toggleForceUnknownProfileError: () =>
    set((s) => ({ forceUnknownProfileError: !s.forceUnknownProfileError })),
  eimiCountdownDebug: "real",
  cycleEimiCountdownDebug: () =>
    set((s) => ({
      eimiCountdownDebug:
        EIMI_COUNTDOWN_CYCLE[
          (EIMI_COUNTDOWN_CYCLE.indexOf(s.eimiCountdownDebug) + 1) %
            EIMI_COUNTDOWN_CYCLE.length
        ],
    })),
  preferenceMatchDebug: "real",
  cyclePreferenceMatchDebug: () =>
    set((s) => ({
      preferenceMatchDebug:
        PREFERENCE_MATCH_CYCLE[
          (PREFERENCE_MATCH_CYCLE.indexOf(s.preferenceMatchDebug) + 1) %
            PREFERENCE_MATCH_CYCLE.length
        ],
    })),
}));
